import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ArrowLeft, CheckCircle2, FileText, Sparkles } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import type { ProfileSectionSlug } from '../../types/profile';
import { toast } from 'sonner';
import { useCandidateProfileSection, useUpdateCandidateProfileSection } from '../../hooks/useCandidateProfile';
import { useSkillsSearch } from '../../hooks/useSkills';
import {
  uploadCandidateCv,
  MAX_UPLOAD_BYTES,
  fetchUploadStatus,
  deleteUpload,
  type UploadStatusResponse,
  type UploadedFileMeta,
} from '../../api/uploads';
import { parseUploadedFileMeta } from '../../utils/profile';

type FieldConfig = {
  id: string;
  label: string;
  placeholder: string;
  type?: 'textarea' | 'input' | 'file';
};

type SectionConfig = {
  title: string;
  description: string;
  fields: FieldConfig[];
  tips: string[];
  allowMultiple?: boolean;
  entryLabel?: string;
};

type SectionEntry = Record<string, string> & { _id: string };

function SkillInputWithSuggestions({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const skills = useSkillsSearch(value);
  const suggestions = skills.data ?? [];

  return (
    <div className="space-y-2">
      <Input
        className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {value.trim().length >= 2 && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {suggestions.slice(0, 8).map((skill) => (
            <Badge
              key={skill.id}
              className="cursor-pointer border-white/20 bg-white/5 text-xs text-gray-100 hover:bg-white/10"
              onClick={() => onChange(skill.preferredLabel)}
            >
              {skill.preferredLabel}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

const createEntry = (config: SectionConfig): SectionEntry => {
  const base: Record<string, string> = {};
  config.fields.forEach((field) => {
    base[field.id] = '';
  });

  return {
    _id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    ...base,
  };
};

const sectionConfigs: Record<ProfileSectionSlug, SectionConfig> = {
  'personal-info': {
    title: 'Personal Info',
    description: 'Keep your key contact information up to date so employers can reach you.',
    fields: [
      { id: 'fullName', label: 'Full Name', placeholder: 'e.g., Sarah Johnson' },
      { id: 'email', label: 'Email Address', placeholder: 'sarah@example.com' },
      { id: 'location', label: 'Location', placeholder: 'City, Country' },
      { id: 'summary', label: 'Professional Summary', placeholder: 'Brief summary about your experience', type: 'textarea' },
      { id: 'resumeUrl', label: 'CV / Resume', placeholder: '', type: 'file' },
    ],
    tips: [
      'Use a professional headline that highlights your expertise.',
      'Make sure your location is accurate to receive local opportunities.',
      'Upload a recent CV so recruiters can review your experience quickly.',
    ],
  },
  'work-experience': {
    title: 'Work Experience',
    description: 'Showcase your most recent roles and measurable impact.',
    allowMultiple: true,
    entryLabel: 'role',
    fields: [
      { id: 'title', label: 'Job Title', placeholder: 'e.g., Senior Care Assistant' },
      { id: 'company', label: 'Company', placeholder: 'Employer name' },
      { id: 'tenure', label: 'Tenure', placeholder: 'Jan 2022 - Present' },
      { id: 'achievements', label: 'Key Achievements', placeholder: 'Describe outcomes and impact', type: 'textarea' },
    ],
    tips: [
      'List achievements with metrics (patients served, satisfaction scores, etc.).',
      'Add multiple roles to highlight career progression.',
    ],
  },
  education: {
    title: 'Education',
    description: 'Include your highest level of education and relevant certifications.',
    allowMultiple: true,
    entryLabel: 'education record',
    fields: [
      { id: 'institution', label: 'Institution', placeholder: 'e.g., University of Leeds' },
      { id: 'qualification', label: 'Qualification', placeholder: 'BSc Nursing' },
      { id: 'graduationDate', label: 'Graduation Date', placeholder: 'July 2020' },
      { id: 'details', label: 'Additional Details', placeholder: 'Honours, awards, specialization', type: 'textarea' },
    ],
    tips: [
      'Mention ongoing studies so employers know you are upskilling.',
      'If you completed professional training, add it here or under certifications.',
    ],
  },
  skills: {
    title: 'Skills',
    description: 'Add both hard and soft skills so the AI matcher can find perfect roles.',
    allowMultiple: true,
    entryLabel: 'skill',
    fields: [
      { id: 'skill', label: 'Skill', placeholder: 'e.g., Dementia Care' },
      { id: 'proficiency', label: 'Proficiency / Notes', placeholder: 'Expert level, 5+ years', type: 'textarea' },
    ],
    tips: [
      'Aim for at least 10 skills to unlock the profile strength bonus.',
      'Prioritise skills mentioned in the jobs you target.',
    ],
  },
  certifications: {
    title: 'Certifications',
    description: 'Upload or list credentials that prove your expertise.',
    allowMultiple: true,
    entryLabel: 'certification',
    fields: [
      { id: 'certName', label: 'Certification Name', placeholder: 'e.g., Level 3 Diploma in Health & Social Care' },
      { id: 'issuer', label: 'Issuing Organisation', placeholder: 'Awarding body' },
      { id: 'issueDate', label: 'Issue Date', placeholder: 'Month / Year' },
      { id: 'notes', label: 'Notes', placeholder: 'License number, expiry, etc.', type: 'textarea' },
    ],
    tips: [
      'Attach documents or add verification links for quicker employer trust.',
      'Expired licenses should be renewed or clearly marked.',
    ],
  },
  'portfolio-links': {
    title: 'Portfolio & Links',
    description: 'Share evidence of your work: portfolios, references, or personal websites.',
    allowMultiple: true,
    entryLabel: 'link',
    fields: [
      { id: 'url', label: 'Website / Portfolio URL', placeholder: 'https://myportfolio.com' },
      { id: 'description', label: 'Description', placeholder: 'What should employers look at?', type: 'textarea' },
    ],
    tips: [
      'Quality beats quantity: add 2–3 strong links.',
      'Preview your links to ensure they are public and mobile-friendly.',
    ],
  },
};

export function ProfileSectionEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sectionId } = useParams<{ sectionId: ProfileSectionSlug }>();
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<SectionEntry[]>([]);
  const [uploadStates, setUploadStates] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const sessionUploadsRef = useRef<Set<string>>(new Set());
  const hasUserEditedRef = useRef(false);
  const persistedSnapshotRef = useRef<string | null>(null);

  const sectionSlug =
    sectionId ??
    ((location.state as { sectionSlug?: ProfileSectionSlug } | null)?.sectionSlug ?? 'personal-info');

  const section = useMemo(() => sectionConfigs[sectionSlug], [sectionSlug]);

  const { data: persistedEntries = [], isPending } = useCandidateProfileSection(sectionSlug);
  const updateMutation = useUpdateCandidateProfileSection(sectionSlug);

  useEffect(() => {
    if (!section || isPending) return;
    const snapshot = JSON.stringify(persistedEntries ?? []);
    const persistedChanged = snapshot !== persistedSnapshotRef.current;
    if (persistedChanged) {
      persistedSnapshotRef.current = snapshot;
    }
    if (!persistedChanged && hasUserEditedRef.current) {
      return;
    }
    if (persistedEntries.length) {
      setEntries(
        persistedEntries.map((entry) => ({
          _id:
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : Math.random().toString(36).slice(2),
          ...section.fields.reduce(
            (acc, field) => ({
              ...acc,
              [field.id]: entry[field.id] ?? '',
            }),
            {} as Record<string, string>,
          ),
        })),
      );
      hasUserEditedRef.current = false;
    } else {
      setEntries((prev) => {
        if (prev.length > 0 && hasUserEditedRef.current) {
          return prev;
        }
        return [createEntry(section)];
      });
    }
  }, [sectionSlug, section, persistedEntries, isPending]);

  useEffect(() => {
    hasUserEditedRef.current = false;
  }, [sectionSlug]);

  useEffect(() => {
    const uploadsSet = sessionUploadsRef.current;
    return () => {
      if (!uploadsSet.size) return;
      const uploads = Array.from(uploadsSet);
      uploadsSet.clear();
      uploads.forEach((uploadId) => {
        void deleteUpload(uploadId).catch((error) => {
          console.warn('[profile] failed to cleanup unsaved upload', { uploadId, error });
        });
      });
    };
  }, []);

  const pendingUploads = useMemo(() => {
    if (!section) return [];
    const pending: Array<{ entryId: string; fieldId: string; uploadId: string }> = [];
    entries.forEach((entry) => {
      section.fields.forEach((field) => {
        if (field.type !== 'file') return;
        const meta = parseUploadedFileMeta(entry[field.id]);
        if (meta?.uploadId && meta.scanStatus === 'pending') {
          pending.push({ entryId: entry._id, fieldId: field.id, uploadId: meta.uploadId });
        }
      });
    });
    return pending;
  }, [entries, section]);

  const pollingRef = useRef<{
    timer?: number;
    delay: number;
    uploads: Array<{ entryId: string; fieldId: string; uploadId: string }>;
  }>({ timer: undefined, delay: 4000, uploads: [] });

  useEffect(() => {
    // Keep a stable list to avoid restarting timers unnecessarily
    pollingRef.current.uploads = pendingUploads;
    const cleanupRef = pollingRef.current;
    // Stop polling if nothing is pending
    if (!pendingUploads.length || !section) {
      if (cleanupRef.timer) {
        window.clearTimeout(cleanupRef.timer);
        cleanupRef.timer = undefined;
      }
      return;
    }

    const refresh = async () => {
      const current = pollingRef.current.uploads;
      if (!current.length) return;

      const backoff = pollingRef.current.delay;
      const uniqueUploads = Array.from(
        new Map(current.map((item) => [item.uploadId, item])).values(),
      );

      try {
        const updates = await Promise.all(
          uniqueUploads.map(async (item) => {
            try {
              const status = await fetchUploadStatus(item.uploadId);
              return { ...item, status };
            } catch {
              return null;
            }
          }),
        );
        const updateMap = new Map<string, UploadStatusResponse>();
        updates.forEach((update) => {
          if (!update?.status) return;
          updateMap.set(`${update.entryId}:${update.fieldId}`, update.status);
        });
        if (!updateMap.size) {
          pollingRef.current.delay = Math.min(backoff * 2, 30000);
          return;
        }
        pollingRef.current.delay = 4000;

        setEntries((prev) =>
          prev.map((entry) => {
            let mutated = false;
            const nextEntry = { ...entry };
            section.fields.forEach((field) => {
              if (field.type !== 'file') return;
              const key = `${entry._id}:${field.id}`;
              const status = updateMap.get(key);
              if (!status) return;
              const meta = parseUploadedFileMeta(entry[field.id]);
              if (!meta || meta.uploadId !== status.uploadId) return;
              const merged = {
                ...meta,
                fileUrl: status.fileUrl ?? status.publicUrl ?? meta.fileUrl,
                publicUrl: status.publicUrl ?? meta.publicUrl,
                scanStatus: status.scanStatus,
                scanCompletedAt: status.scanCompletedAt ?? meta.scanCompletedAt,
              };
              nextEntry[field.id] = JSON.stringify(merged);
              mutated = true;
            });
            return mutated ? nextEntry : entry;
          }),
        );
      } finally {
        if (pollingRef.current.uploads.length) {
          const nextDelay = pollingRef.current.delay;
          pollingRef.current.timer = window.setTimeout(() => {
            refresh().catch(() => {});
          }, nextDelay);
        } else {
          if (pollingRef.current.timer) {
            window.clearTimeout(pollingRef.current.timer);
            pollingRef.current.timer = undefined;
          }
        }
      }
    };

    if (!pollingRef.current.timer) {
      pollingRef.current.timer = window.setTimeout(() => {
        refresh().catch(() => {});
      }, pollingRef.current.delay);
    }

    return () => {
      if (cleanupRef.timer) {
        window.clearTimeout(cleanupRef.timer);
        cleanupRef.timer = undefined;
      }
    };
  }, [pendingUploads, section]);

  const updateEntry = (entryId: string, fieldId: string, value: string) => {
    hasUserEditedRef.current = true;
    setEntries((prev) =>
      prev.map((entry) => (entry._id === entryId ? { ...entry, [fieldId]: value } : entry)),
    );
  };

  const getUploadKey = (entryId: string, fieldId: string) => `${entryId}-${fieldId}`;

  const setFileInputRef = (key: string) => (node: HTMLInputElement | null) => {
    if (node) {
      fileInputRefs.current[key] = node;
    } else {
      delete fileInputRefs.current[key];
    }
  };

  const triggerFileDialog = (key: string) => {
    fileInputRefs.current[key]?.click();
  };

  const deleteUploadIfNeeded = async (uploadId?: string, notifyOnError = false) => {
    if (!uploadId) return;
    sessionUploadsRef.current.delete(uploadId);
    try {
      await deleteUpload(uploadId);
    } catch (error) {
      console.error(error);
      if (notifyOnError) {
        toast.error('Unable to delete the uploaded file from storage. Please try again.');
      }
    }
  };

  const handleFileUpload = async (entryId: string, fieldId: string, key: string, file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('Files must be 10MB or smaller (PDF, DOC, DOCX).');
      return;
    }
    const existingMeta = parseUploadedFileMeta(entries.find((entry) => entry._id === entryId)?.[fieldId]);
    setUploadStates((prev) => ({ ...prev, [key]: true }));
    try {
      const meta = await uploadCandidateCv(file);
      if (meta.uploadId) {
        sessionUploadsRef.current.add(meta.uploadId);
      }
      updateEntry(entryId, fieldId, JSON.stringify(meta));
      if (existingMeta?.uploadId && existingMeta.uploadId !== meta.uploadId) {
        void deleteUploadIfNeeded(existingMeta.uploadId, true);
      }
      toast.success('CV uploaded');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to upload CV.';
      toast.error(message);
    } finally {
      setUploadStates((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleAddEntry = () => {
    if (!section) return;
    hasUserEditedRef.current = true;
    setEntries((prev) => [...prev, createEntry(section)]);
  };

  const handleRemoveEntry = (entryId: string) => {
    const entry = entries.find((item) => item._id === entryId);
    if (entry && section) {
      const deletions = section.fields
        .filter((field) => field.type === 'file')
        .map((field) => deleteUploadIfNeeded(parseUploadedFileMeta(entry[field.id])?.uploadId, true));
      void Promise.all(deletions);
    }
    setEntries((prev) => {
      if (prev.length <= 1) return prev;
      const filtered = prev.filter((entry) => entry._id !== entryId);
      setUploadStates((current) => {
        const next = { ...current };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${entryId}-`)) {
            delete next[key];
          }
        });
        return next;
      });
      Object.keys(fileInputRefs.current).forEach((key) => {
        if (key.startsWith(`${entryId}-`)) {
          delete fileInputRefs.current[key];
        }
      });
      hasUserEditedRef.current = true;
      return filtered;
    });
  };

  const handleRemoveFile = async (entryId: string, fieldId: string, meta?: UploadedFileMeta | null) => {
    updateEntry(entryId, fieldId, '');
    if (meta?.uploadId) {
      await deleteUploadIfNeeded(meta.uploadId, true);
    }
  };

  if (!section) {
    return (
      <main
        className="flex min-h-screen flex-col items-center justify-center bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)] px-4 text-center"
        aria-label="Profile section missing"
      >
        <p className="text-gray-400 mb-4">This profile section could not be found.</p>
        <Button onClick={() => navigate('/candidate/profile-strength')}>
          Return to profile strength
        </Button>
      </main>
    );
  }

  const handleSave = () => {
    setSaving(true);
    updateMutation
      .mutateAsync(
        entries.map((entry) => {
          const { _id, ...rest } = entry;
          return rest;
        }),
      )
      .then(() => {
        toast.success('Section updated');
        hasUserEditedRef.current = false;
        sessionUploadsRef.current.clear();
        navigate('/candidate/profile-strength', { state: { sectionUpdated: sectionSlug } });
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Unable to save section right now.';
        toast.error(message);
      })
      .finally(() => setSaving(false));
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="profile-section-heading"
    >
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.85)] backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Button
            variant="ghost"
            className="text-gray-300 hover:text-white"
            onClick={() => navigate('/candidate/profile-strength')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Profile Strength
          </Button>
          <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
            Edit Mode
          </Badge>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-10 space-y-8">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.4em] text-blue-200/80">Profile section</p>
          <h1 id="profile-section-heading" className="text-3xl font-semibold text-gold-gradient">{section.title}</h1>
          <p className="text-gray-300">{section.description}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]" aria-label="Profile editor content">
          <Card className="role-surface-candidate border-white/10 bg-white/[0.02]">
            <CardHeader>
              <CardTitle className="text-white">Edit details</CardTitle>
              <CardDescription className="text-gray-400">
                Changes are saved privately until you publish updates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {isPending ? (
                <div className="space-y-3">
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div key={`section-skeleton-${index}`} className="space-y-4 rounded-lg border border-white/10 bg-white/[0.03] p-4">
                      <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
                      <div className="h-10 w-full animate-pulse rounded bg-white/10" />
                      <div className="h-10 w-11/12 animate-pulse rounded bg-white/10" />
                    </div>
                  ))}
                </div>
              ) : (
                entries.map((entry, entryIndex) => (
                  <div key={entry._id} className="space-y-4 rounded-lg border border-white/10 bg-white/[0.03] p-4">
                    {section.allowMultiple && (
                      <div className="flex items-center justify-between text-sm text-gray-300">
                        <span>
                          {section.entryLabel ? section.entryLabel.replace(/^\w/, (c) => c.toUpperCase()) : 'Entry'}{' '}
                          #{entryIndex + 1}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-300 hover:text-red-200"
                          disabled={entries.length === 1}
                          onClick={() => handleRemoveEntry(entry._id)}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    {section.fields.map((field) => {
                      const key = `${entry._id}-${field.id}`;
                      if (field.type === 'file') {
                        const fileMeta = parseUploadedFileMeta(entry[field.id]);
                        const uploading = Boolean(uploadStates[key]);
                        const scanStatus = fileMeta?.scanStatus ?? 'clean';
                        const statusLabel =
                          scanStatus === 'clean'
                            ? 'Ready'
                            : scanStatus === 'pending'
                            ? 'Scanning'
                            : 'Blocked';
                        const statusTone =
                          scanStatus === 'clean'
                            ? 'text-emerald-300'
                            : scanStatus === 'pending'
                            ? 'text-amber-300'
                            : 'text-red-300';
                        const downloadDisabled = scanStatus !== 'clean';
                        return (
                          <div key={key} className="space-y-2">
                            <Label className="text-white">{field.label}</Label>
                            {fileMeta ? (
                              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm text-gray-200">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <p className="flex items-center gap-2 text-white">
                                      <FileText className="w-4 h-4" />
                                      {fileMeta.fileName ?? 'Uploaded CV'}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Uploaded{' '}
                                      {fileMeta.uploadedAt
                                        ? new Date(fileMeta.uploadedAt).toLocaleDateString()
                                        : ''}
                                    </p>
                                    <p className={`text-xs ${statusTone}`}>
                                      {statusLabel}
                                      {scanStatus === 'pending' && ' · Security scan in progress'}
                                      {scanStatus === 'blocked' && ' · Remove and upload a new file'}
                                    </p>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      asChild
                                      size="sm"
                                      variant="outline"
                                      className="border-white/20 text-white hover:bg-white/5"
                                      disabled={downloadDisabled}
                                    >
                                      <a
                                        href={downloadDisabled ? undefined : fileMeta.fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-disabled={downloadDisabled}
                                      >
                                        Download
                                      </a>
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-300 hover:text-red-100"
                                      onClick={() => {
                                        void handleRemoveFile(entry._id, field.id, fileMeta);
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400">
                                Upload a recent CV to share with employers.
                              </p>
                            )}
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              className="hidden"
                              ref={setFileInputRef(key)}
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                  void handleFileUpload(entry._id, field.id, key, file);
                                }
                                event.target.value = '';
                              }}
                            />
                            <div className="flex flex-col gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="border-dashed border-white/20 text-white hover:bg-white/5"
                                onClick={() => triggerFileDialog(key)}
                                disabled={uploading}
                              >
                                {uploading ? 'Uploading…' : fileMeta ? 'Replace CV' : 'Upload CV'}
                              </Button>
                              <p className="text-xs text-gray-500">
                                PDF, DOC, or DOCX only • Maximum 10MB per file.
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={key} className="space-y-2">
                          <Label className="text-white">{field.label}</Label>
                          {field.type === 'textarea' ? (
                            <Textarea
                              rows={4}
                              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                              placeholder={field.placeholder}
                              value={entry[field.id] ?? ''}
                              onChange={(event) => updateEntry(entry._id, field.id, event.target.value)}
                            />
                          ) : field.id === 'skill' ? (
                            <SkillInputWithSuggestions
                              value={entry[field.id] ?? ''}
                              placeholder={field.placeholder}
                              onChange={(val) => updateEntry(entry._id, field.id, val)}
                            />
                          ) : (
                            <Input
                              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                              placeholder={field.placeholder}
                              value={entry[field.id] ?? ''}
                              onChange={(event) => updateEntry(entry._id, field.id, event.target.value)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}

              {section.allowMultiple && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-dashed border-white/20 text-white hover:bg-white/5"
                  onClick={handleAddEntry}
                  disabled={isPending}
                >
                  Add another {section.entryLabel ?? 'entry'}
                </Button>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleSave}
                  className="bg-gold-gradient text-black hover:brightness-110"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  onClick={() => navigate('/candidate/profile-strength')}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/[0.015] border border-white/10 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#ba9036]" />
                Pro tips
              </CardTitle>
              <CardDescription className="text-gray-400">
                Complete this section to unlock the full profile strength bonus.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {section.tips.map((tip, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-gray-300"
                >
                  <div className="mb-1 flex items-center gap-2 text-[#ba9036]">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">Tip</span>
                  </div>
                  <p>{tip}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
